import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Restore user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      // Restore user from localStorage immediately for faster UX
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Failed to parse stored user:', e);
        }
      }
      
      // Then validate and refresh user data from server
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (currentToken) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`);
      const userData = {
        id: res.data.id,
        full_name: res.data.full_name,
        email: res.data.email,
        organization_id: res.data.organization_id,
        role: res.data.role,
        company_name: res.data.company_name
      };
      setUser(userData);
      // Store user in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (err) {
      console.error("Auth Error", err);
      // Only logout if it's a 401 (unauthorized) error
      if (err.response?.status === 401) {
        clearAuth();
      }
      // For other errors (network, etc.), keep the user logged in with cached data
    } finally {
      setLoading(false);
    }
  };

  const clearAuth = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organization_id');
    delete axios.defaults.headers.common['Authorization'];
  };

  const login = async (email, password) => {
    try {
      console.log("Attempting login for:", email);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, { email, password });
      console.log("Login successful:", res.data);
      
      const newToken = res.data.access_token;
      const userData = { 
        id: res.data.user_id,
        full_name: res.data.user_name, 
        email: res.data.email,
        organization_id: res.data.organization_id,
        role: res.data.role || 'user'
      };
      
      setToken(newToken);
      setUser(userData);
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('organization_id', res.data.organization_id);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      toast.success(`Welcome back, ${res.data.user_name}`);
      return true;
    } catch (err) {
      console.error("Login failed:", err);
      toast.error(err.response?.data?.detail || "Login failed. Please check console.");
      return false;
    }
  };

  const signup = async (data) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, data);
      
      const newToken = res.data.access_token;
      const userData = { 
        id: res.data.user_id,
        full_name: res.data.user_name, 
        email: res.data.email,
        organization_id: res.data.organization_id,
        role: res.data.role || 'admin'
      };
      
      setToken(newToken);
      setUser(userData);
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('organization_id', res.data.organization_id);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      toast.success("Account created successfully");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
      return false;
    }
  };

  const logout = () => {
    clearAuth();
    toast.success("Logged out successfully");
  };

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
